/**
 * Authentication boundary for the frontend.
 *
 * When auth is enabled:
 * - authenticated users render the app directly
 * - anonymous users render a temporary landing page with a sign-in button
 *
 * When auth is disabled:
 * - the app renders directly with no auth gate
 */

import React, { Suspense, lazy, useEffect, useCallback, useState, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import {
  endGuestAccess,
  getGuestAccessStatus,
  setAuthTokenGetter,
  startGuestAccess,
} from './services/api';
import { isAuthEnabled, loadAmplifyAuth } from './auth/amplifyRuntime';
import AnonymousLanding from './components/landing/AnonymousLanding';
import GuestAccessScreen from './components/guest/GuestAccessScreen';
import LecturesPage from './components/landing/LecturesPage';
import AboutOverlay from './components/overlays/AboutOverlay';
import ClientProfilesOverlay from './components/overlays/ClientProfilesOverlay';
import CounsellorsOverlay from './components/overlays/CounsellorsOverlay';
import TutorialOverlay from './components/overlays/TutorialOverlay';
import AutomatedMiscOverlay from './components/overlays/AutomatedMiscOverlay';
import { COUNSELLOR_OVERLAY_CONFIG, COUNSELLOR_OVERLAY_IDS } from './components/overlays/counsellorOverlayConfig';
import './components/overlays/OverlayShell.css';
import './components/overlays/OverlayContent.css';
import { translate } from './LanguageContext';
import enTranslations from './locales/en.json';
import useClientProfiles, { getMetaKeyForPersona } from './hooks/useClientProfiles';
import {
  didViewportRevealKeyboard,
  shouldReloadAfterKeyboardLogin,
} from './auth/mobileKeyboardRecovery';
import { getPostAuthDestination } from './auth/postAuthDestination';
import { LECTURES } from './components/landing/LecturesPage';

const ENABLE_AUTH = isAuthEnabled();

const clientCodingRows = [
  ['Desire+', 'Change Talk', 'Client expresses desire to change (e.g., "I want to quit")'],
  ['Ability+', 'Change Talk', 'Client expresses optimism about ability to change'],
  ['Reason+', 'Change Talk', 'Client provides reasons for changing'],
  ['Need+', 'Change Talk', 'Client expresses need to change'],
  ['Commitment+', 'Change Talk', 'Client expresses commitment to change'],
  ['Activation+', 'Change Talk', 'Client leans toward action or suggests alternatives'],
  ['TakingSteps+', 'Change Talk', 'Client mentions recent steps toward change'],
  ['Other+', 'Change Talk', 'Supports change but does not fit other categories'],
  ['Desire-', 'Sustain Talk', 'Client expresses desire to maintain behavior'],
  ['Ability-', 'Sustain Talk', 'Client expresses pessimism about ability to change'],
  ['Reason-', 'Sustain Talk', 'Client provides reasons for maintaining behavior'],
  ['Need-', 'Sustain Talk', 'Client expresses need to maintain behavior'],
  ['Commitment-', 'Sustain Talk', 'Client expresses commitment to maintain behavior'],
  ['Activation-', 'Sustain Talk', 'Client leans toward inaction'],
  ['TakingSteps-', 'Sustain Talk', 'Client mentions steps reinforcing behavior'],
  ['Other-', 'Sustain Talk', 'Supports maintaining behavior but does not fit other categories'],
  ['Neutral', 'Neutral', 'Statement that neither supports nor opposes change'],
];

const counsellorCodingRows = [
  ['AF', 'MI-Adherent', 'Affirm - Supportive statement about client strengths'],
  ['SR', 'MI-Adherent', 'Simple Reflection - Rephrasing client content'],
  ['CR', 'MI-Adherent', 'Complex Reflection - Reflection adding meaning'],
  ['Seek', 'MI-Adherent', 'Seek Collaboration - Inviting client input'],
  ['Emphasize', 'MI-Adherent', 'Emphasize Autonomy - Supporting client choice'],
  ['Persuade with', 'MI-Adherent', 'Persuade with Permission - Advice with permission'],
  ['Persuade', 'MI-Inconsistent', 'Persuade - Advice without permission'],
  ['Confront', 'MI-Inconsistent', 'Confront - Disagreeing, arguing, or shaming'],
  ['GI', 'Other', 'Giving Information - Neutral information sharing'],
  ['Q', 'Other', 'Question - Asking for information from client'],
  ['NC', 'Other', 'Non-Codable - Utterances that cannot be classified'],
];
const AuthLoginOverlay = lazy(() => import('./auth/AuthLoginOverlay'));
const ExpandedSectionModal = lazy(() => import('./components/dialogs/ExpandedSectionModal'));

// Fail-closed defaults: only used when a consumer renders outside the
// provider (tests/misconfig). Defaulting to "authenticated" would silently
// hide auth regressions, so default to unauthenticated. The real app always
// gets the live value from AuthContext.Provider below.
const AuthContext = createContext({
  requireAuth: () => Promise.resolve(false),
  authenticated: false,
  guest: false,
  signOut: () => {},
});

const LANDING_BASE_PATH = '/landing';
const GUEST_DEMO_PATH = '/demo';

function getLandingRouteState(pathname) {
  if (pathname === LANDING_BASE_PATH || pathname === `${LANDING_BASE_PATH}/`) {
    return { showLectures: false, overlay: null, lectureId: null };
  }
  if (pathname === `${LANDING_BASE_PATH}/about`) {
    return { showLectures: false, overlay: 'about', lectureId: null };
  }
  if (pathname === `${LANDING_BASE_PATH}/clients`) {
    return { showLectures: false, overlay: 'clients', lectureId: null };
  }
  if (pathname === `${LANDING_BASE_PATH}/counsellors`) {
    return { showLectures: false, overlay: 'counsellor-freeform', lectureId: null };
  }
  for (const [overlayId, config] of Object.entries(COUNSELLOR_OVERLAY_CONFIG)) {
    if (pathname === config.landingPath) {
      return { showLectures: false, overlay: overlayId, lectureId: null };
    }
  }
  if (pathname === `${LANDING_BASE_PATH}/tutorial`) {
    return { showLectures: false, overlay: 'tutorial', lectureId: null };
  }
  if (pathname === `${LANDING_BASE_PATH}/coding`) {
    return { showLectures: false, overlay: 'coding', lectureId: null };
  }
  if (pathname === `${LANDING_BASE_PATH}/lectures` || pathname === `${LANDING_BASE_PATH}/lectures/`) {
    return { showLectures: true, overlay: null, lectureId: null };
  }
  const lectureMatch = pathname.match(/^\/landing\/lectures\/([a-z-]+)$/);
  if (lectureMatch && LECTURES.some((lecture) => lecture.id === lectureMatch[1])) {
    return { showLectures: true, overlay: null, lectureId: lectureMatch[1] };
  }
  return null;
}

function getLandingPath({ showLectures = false, overlay = null, lectureId = null } = {}) {
  if (showLectures) {
    return lectureId ? `${LANDING_BASE_PATH}/lectures/${lectureId}` : `${LANDING_BASE_PATH}/lectures`;
  }
  if (overlay === 'about') return `${LANDING_BASE_PATH}/about`;
  if (overlay === 'clients') return `${LANDING_BASE_PATH}/clients`;
  if (overlay && COUNSELLOR_OVERLAY_CONFIG[overlay]) return COUNSELLOR_OVERLAY_CONFIG[overlay].landingPath;
  if (overlay === 'counsellors') return COUNSELLOR_OVERLAY_CONFIG['counsellor-freeform'].landingPath;
  if (overlay === 'tutorial') return `${LANDING_BASE_PATH}/tutorial`;
  if (overlay === 'coding') return `${LANDING_BASE_PATH}/coding`;
  return LANDING_BASE_PATH;
}

export const useAuth = () => useContext(AuthContext);

function AuthWrapper({ children }) {
  const [showLogin, setShowLogin] = useState(false);
  const [showLectures, setShowLectures] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showClients, setShowClients] = useState(false);
  const [activeCounsellorOverlay, setActiveCounsellorOverlay] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showCoding, setShowCoding] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [activeLectureId, setActiveLectureId] = useState(null);
  const t = (key, vars) => translate(enTranslations, key, vars);
  const clientProfilesData = useClientProfiles(showClients ? 'client' : null);
  const [authenticated, setAuthenticated] = useState(!ENABLE_AUTH);
  const [authReady, setAuthReady] = useState(!ENABLE_AUTH);
  const [authMode, setAuthMode] = useState(!ENABLE_AUTH ? 'disabled' : null);
  const [guestAccess, setGuestAccess] = useState(null);
  const [guestEntryStatus, setGuestEntryStatus] = useState('loading');
  const authenticatedRef = useRef(!ENABLE_AUTH);
  const pendingResolveRef = useRef(null);
  const interceptorRef = useRef(null);
  const preLoginViewportHeightRef = useRef(null);
  const loginKeyboardOpenedRef = useRef(false);
  const postAuthDestinationRef = useRef(getPostAuthDestination(window.location.pathname));

  const expireGuestAccess = useCallback((status = 'expired') => {
    authenticatedRef.current = false;
    setAuthenticated(false);
    setAuthMode(null);
    setGuestAccess(null);
    setGuestEntryStatus(status);
    setAuthTokenGetter(null);
    window.history.replaceState({ guestDemo: true }, '', GUEST_DEMO_PATH);
  }, []);

  // Prefer an existing Cognito session. Otherwise resume a valid guest cookie,
  // or create a guest identity when the browser entered through /demo.
  useEffect(() => {
    if (!ENABLE_AUTH) return;
    (async () => {
      try {
        const { getCurrentUser } = await loadAmplifyAuth();
        await getCurrentUser();
        authenticatedRef.current = true;
        setAuthenticated(true);
        setAuthMode('cognito');
        setupInterceptor();
      } catch {
        try {
          let guest = await getGuestAccessStatus();
          if (!guest.active && window.location.pathname === GUEST_DEMO_PATH) {
            guest = await startGuestAccess();
          }
          if (guest.active) {
            authenticatedRef.current = true;
            setAuthenticated(true);
            setAuthMode('guest');
            setGuestAccess(guest);
            setGuestEntryStatus('loading');
            if (window.location.pathname === GUEST_DEMO_PATH) {
              window.history.replaceState({ guestDemo: true }, '', '/');
            }
          } else {
            authenticatedRef.current = false;
            setAuthenticated(false);
            setAuthMode(null);
            setGuestEntryStatus(guest.enabled === false ? 'disabled' : guest.status || 'unavailable');
          }
        } catch (error) {
          authenticatedRef.current = false;
          setAuthenticated(false);
          setAuthMode(null);
          const code = error?.response?.data?.code;
          setGuestEntryStatus(
            code === 'guest_demo_disabled'
              ? 'disabled'
              : code === 'guest_access_expired'
                ? 'expired'
                : 'unavailable'
          );
        }
      } finally {
        setAuthReady(true);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setupInterceptor = useCallback(() => {
    if (interceptorRef.current !== null) return;
    const attachToken = async (config) => {
      try {
        const { fetchAuthSession } = await loadAmplifyAuth();
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (err) {
        console.error('Failed to get auth session:', err);
      }
      return config;
    };
    interceptorRef.current = axios.interceptors.request.use(attachToken);

    // Register auth token getter for non-axios requests (e.g. fetch-based SSE)
    setAuthTokenGetter(async () => {
      try {
        const { fetchAuthSession } = await loadAmplifyAuth();
        const session = await fetchAuthSession();
        return session.tokens?.idToken?.toString() || null;
      } catch (_) {
        return null;
      }
    });
  }, []);

  useEffect(() => {
    if (authMode !== 'guest' || !guestAccess?.expires_at) return undefined;
    const expiresAtMs = new Date(guestAccess.expires_at).getTime();
    const remainingMs = expiresAtMs - Date.now();
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
      expireGuestAccess('expired');
      return undefined;
    }
    const timeoutId = window.setTimeout(() => expireGuestAccess('expired'), remainingMs);
    return () => window.clearTimeout(timeoutId);
  }, [authMode, expireGuestAccess, guestAccess?.expires_at]);

  useEffect(() => {
    if (authMode !== 'guest') return undefined;
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const code = error?.response?.data?.code;
        if (code === 'guest_access_expired' || code === 'guest_demo_disabled') {
          expireGuestAccess(code === 'guest_demo_disabled' ? 'disabled' : 'expired');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [authMode, expireGuestAccess]);

  const requireAuth = useCallback(() => {
    if (authenticatedRef.current) return Promise.resolve(true);
    return new Promise((resolve) => {
      pendingResolveRef.current = resolve;
      preLoginViewportHeightRef.current = window.visualViewport?.height || window.innerHeight;
      loginKeyboardOpenedRef.current = false;
      setShowLogin(true);
    });
  }, []);

  useEffect(() => {
    if (!showLogin) return undefined;

    const viewport = window.visualViewport;
    const detectKeyboard = () => {
      const baselineHeight = preLoginViewportHeightRef.current;
      const currentHeight = viewport?.height || window.innerHeight;
      if (didViewportRevealKeyboard(baselineHeight, currentHeight)) {
        loginKeyboardOpenedRef.current = true;
      }
    };
    const detectManualCredentialEntry = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.closest('.auth-overlay')) return;

      const typedCharacter = event.type === 'keydown'
        ? typeof event.key === 'string' && event.key.length === 1
        : event.inputType === 'insertText'
          && typeof event.data === 'string'
          && event.data.length <= 1;
      if (event.isTrusted && typedCharacter) {
        loginKeyboardOpenedRef.current = true;
      }
    };

    detectKeyboard();
    viewport?.addEventListener('resize', detectKeyboard);
    window.addEventListener('resize', detectKeyboard);
    document.addEventListener('keydown', detectManualCredentialEntry, true);
    document.addEventListener('beforeinput', detectManualCredentialEntry, true);
    return () => {
      viewport?.removeEventListener('resize', detectKeyboard);
      window.removeEventListener('resize', detectKeyboard);
      document.removeEventListener('keydown', detectManualCredentialEntry, true);
      document.removeEventListener('beforeinput', detectManualCredentialEntry, true);
    };
  }, [showLogin]);

  const handleAuthSuccess = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const destination = postAuthDestinationRef.current || '/';

    // Chrome on iOS can retain a native WKWebView keyboard pan after the login
    // DOM is replaced, even though all web-exposed scroll/viewport offsets read
    // as zero. A fresh navigation is the only deterministic reset. Cognito has
    // already persisted the authenticated session before this callback runs.
    if (shouldReloadAfterKeyboardLogin({
      keyboardOpened: loginKeyboardOpenedRef.current,
      userAgent: window.navigator.userAgent,
      isSingleColumn: window.matchMedia('(max-width: 816px)').matches,
    })) {
      window.location.replace(destination);
      return;
    }

    window.history.replaceState({}, '', destination);
    preLoginViewportHeightRef.current = null;
    loginKeyboardOpenedRef.current = false;
    authenticatedRef.current = true;
    setAuthenticated(true);
    setAuthMode('cognito');
    setGuestAccess(null);
    setShowLogin(false);
    setupInterceptor();
    if (pendingResolveRef.current) {
      pendingResolveRef.current(true);
      pendingResolveRef.current = null;
    }
  }, [setupInterceptor]);

  const handleDismiss = useCallback(() => {
    setShowLogin(false);
    preLoginViewportHeightRef.current = null;
    loginKeyboardOpenedRef.current = false;
    if (pendingResolveRef.current) {
      pendingResolveRef.current(false);
      pendingResolveRef.current = null;
    }
  }, []);

  const signOut = useCallback(async () => {
    if (!ENABLE_AUTH) return;
    if (authMode === 'guest') {
      try {
        await endGuestAccess();
      } catch (err) {
        console.error('Guest sign out error:', err);
      }
      authenticatedRef.current = false;
      setAuthenticated(false);
      setAuthMode(null);
      setGuestAccess(null);
      setGuestEntryStatus('loading');
      setAuthTokenGetter(null);
      window.history.replaceState({ landing: true }, '', LANDING_BASE_PATH);
      return;
    }
    try {
      const { signOut: amplifySignOut } = await loadAmplifyAuth();
      await amplifySignOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
    authenticatedRef.current = false;
    setAuthenticated(false);
    setAuthMode(null);
    if (interceptorRef.current !== null) {
      axios.interceptors.request.eject(interceptorRef.current);
      interceptorRef.current = null;
    }
    setAuthTokenGetter(null);
  }, [authMode]);

  const applyLandingRouteState = useCallback((routeState) => {
    setShowLectures(routeState.showLectures);
    setShowAbout(routeState.overlay === 'about');
    setShowClients(routeState.overlay === 'clients');
    setActiveCounsellorOverlay(COUNSELLOR_OVERLAY_IDS.includes(routeState.overlay) ? routeState.overlay : null);
    setShowTutorial(routeState.overlay === 'tutorial');
    setShowCoding(routeState.overlay === 'coding');
    setActiveLectureId(routeState.lectureId);
    setExpandedSection(null);
  }, []);

  const updateLandingRoute = useCallback((routeState, replace = false) => {
    const path = getLandingPath(routeState);
    const method = replace ? 'replaceState' : 'pushState';
    window.history[method]({ landing: true, ...routeState }, '', path);
    applyLandingRouteState(routeState);
  }, [applyLandingRouteState]);

  useEffect(() => {
    if (
      !authReady
      || authenticated
      || window.location.pathname === GUEST_DEMO_PATH
    ) {
      return undefined;
    }

    const syncFromLocation = (replace = false) => {
      if (!window.location.pathname.startsWith(LANDING_BASE_PATH)) {
        postAuthDestinationRef.current = getPostAuthDestination(window.location.pathname);
      }
      const routeState = getLandingRouteState(window.location.pathname);
      if (routeState) {
        if (replace && !window.history.state?.landing) {
          window.history.replaceState({ landing: true, ...routeState }, '', getLandingPath(routeState));
        }
        applyLandingRouteState(routeState);
        return;
      }
      const defaultState = { showLectures: false, overlay: null, lectureId: null };
      if (replace) {
        window.history.replaceState({ landing: true, ...defaultState }, '', getLandingPath(defaultState));
      }
      applyLandingRouteState(defaultState);
    };

    syncFromLocation(true);

    const handlePopState = () => {
      syncFromLocation(false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [applyLandingRouteState, authReady, authenticated]);

  useEffect(() => {
    if (!authenticated) return;
    if (
      window.location.pathname.startsWith(LANDING_BASE_PATH)
      || window.location.pathname === GUEST_DEMO_PATH
    ) {
      window.history.replaceState({}, '', '/');
    }
  }, [authenticated]);

  const openLandingOverlay = useCallback((overlay) => {
    updateLandingRoute({ showLectures: false, overlay, lectureId: null });
  }, [updateLandingRoute]);

  const closeLandingOverlay = useCallback(() => {
    updateLandingRoute({ showLectures: false, overlay: null, lectureId: null });
  }, [updateLandingRoute]);

  const openLandingLectures = useCallback(() => {
    updateLandingRoute({ showLectures: true, overlay: null, lectureId: null });
  }, [updateLandingRoute]);

  const closeLandingLectures = useCallback(() => {
    updateLandingRoute({ showLectures: false, overlay: null, lectureId: null });
  }, [updateLandingRoute]);

  const openLandingLectureVideo = useCallback((lectureId) => {
    updateLandingRoute({ showLectures: true, overlay: null, lectureId });
  }, [updateLandingRoute]);

  const closeLandingLectureVideo = useCallback(() => {
    updateLandingRoute({ showLectures: true, overlay: null, lectureId: null });
  }, [updateLandingRoute]);

  const handleGuestSignIn = useCallback(() => {
    postAuthDestinationRef.current = '/';
    setGuestEntryStatus('loading');
    window.history.replaceState({ landing: true }, '', LANDING_BASE_PATH);
    requireAuth();
  }, [requireAuth]);

  if (!authReady) {
    return null;
  }

  const landingOverlayOpen = showAbout || showClients || !!activeCounsellorOverlay || showTutorial || showCoding;

  return (
    <AuthContext.Provider value={{ requireAuth, authenticated, guest: authMode === 'guest', signOut }}>
      {authenticated ? children : null}
      {!authenticated && window.location.pathname === GUEST_DEMO_PATH && (
        <GuestAccessScreen
          status={guestEntryStatus}
          onSignIn={handleGuestSignIn}
        />
      )}
      {!authenticated && window.location.pathname !== GUEST_DEMO_PATH && !showLectures && (
        <AnonymousLanding
          onSignIn={requireAuth}
          onLearnMore={openLandingLectures}
          onAbout={() => openLandingOverlay('about')}
          onClients={() => openLandingOverlay('clients')}
          onCounsellors={() => openLandingOverlay('counsellor-freeform')}
          onTutorial={() => openLandingOverlay('tutorial')}
          onCoding={() => openLandingOverlay('coding')}
          overlayOpen={landingOverlayOpen}
        />
      )}
      {!authenticated && window.location.pathname !== GUEST_DEMO_PATH && showLectures && (
        <LecturesPage
          onBack={closeLandingLectures}
          activeLectureId={activeLectureId}
          onOpenLecture={openLandingLectureVideo}
          onCloseLecture={closeLandingLectureVideo}
        />
      )}
      {showAbout && (
        <div className="App app-overlay-root dark">
          <div className="client-info-overlay" onClick={closeLandingOverlay}>
            <div
              className="client-info-panel client-info-panel--landing"
              role="dialog"
              aria-modal="true"
              aria-labelledby="overlay-heading"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="client-info-close-wrapper">
                <button
                  type="button"
                  className="client-info-close-btn"
                  onClick={closeLandingOverlay}
                  aria-label="Back"
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                    <path d="M12.5 4L7 10l5.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  Back
                </button>
              </div>
              <div className="landing-overlay-content">
                <AboutOverlay t={t} setExpandedSection={setExpandedSection} />
              </div>
            </div>
          </div>
        </div>
      )}
      {showClients && (
        <div className="App app-overlay-root dark">
          <div className="client-info-overlay" onClick={closeLandingOverlay}>
            <div
              className="client-info-panel client-info-panel--landing"
              role="dialog"
              aria-modal="true"
              aria-labelledby="overlay-heading"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="client-info-close-wrapper">
                <button
                  type="button"
                  className="client-info-close-btn"
                  onClick={closeLandingOverlay}
                  aria-label="Back"
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                    <path d="M12.5 4L7 10l5.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  Back
                </button>
              </div>
              <div className="landing-overlay-content">
                <ClientProfilesOverlay
                  t={t}
                  clientProfilesOverlay={{
                    setExpandedSection,
                    clientProfilesLoading: clientProfilesData.clientProfilesLoading,
                    clientProfilesError: clientProfilesData.clientProfilesError,
                    clientProfilesExtended: clientProfilesData.clientProfilesExtended,
                    activeClientProfile: clientProfilesData.activeClientProfile,
                    activeClientDisplayIndex: clientProfilesData.activeClientDisplayIndex,
                    handleClientProfileTabSelect: clientProfilesData.handleClientProfileTabSelect,
                    clientProfileVisible: clientProfilesData.clientProfileVisible,
                    personaList: clientProfilesData.personaList,
                    getMetaKeyForPersona,
                    highlightedMetaKey: clientProfilesData.highlightedMetaKey,
                    setHighlightedMetaKey: clientProfilesData.setHighlightedMetaKey,
                    metadataEntries: clientProfilesData.metadataEntries,
                    motivationList: clientProfilesData.motivationList,
                    barrierList: clientProfilesData.barrierList,
                    nextSteps: clientProfilesData.nextSteps,
                    transcriptLines: clientProfilesData.transcriptLines,
                    handleClientProfileReveal: clientProfilesData.handleClientProfileReveal,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {activeCounsellorOverlay && (
        <div className="App app-overlay-root dark">
          <div className="client-info-overlay" onClick={closeLandingOverlay}>
            <div
              className="client-info-panel client-info-panel--landing"
              role="dialog"
              aria-modal="true"
              aria-labelledby="overlay-heading"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="client-info-close-wrapper">
                <button
                  type="button"
                  className="client-info-close-btn"
                  onClick={closeLandingOverlay}
                  aria-label="Back"
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                    <path d="M12.5 4L7 10l5.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  Back
                </button>
              </div>
              <div className="landing-overlay-content">
                <CounsellorsOverlay t={t} variant={activeCounsellorOverlay} />
              </div>
            </div>
          </div>
        </div>
      )}
      {showTutorial && (
        <div className="App app-overlay-root dark">
          <div className="client-info-overlay" onClick={closeLandingOverlay}>
            <div
              className="client-info-panel client-info-panel--landing"
              role="dialog"
              aria-modal="true"
              aria-labelledby="overlay-heading"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="client-info-close-wrapper">
                <button
                  type="button"
                  className="client-info-close-btn"
                  onClick={closeLandingOverlay}
                  aria-label="Back"
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                    <path d="M12.5 4L7 10l5.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  Back
                </button>
              </div>
              <div className="landing-overlay-content">
                <TutorialOverlay t={t} />
              </div>
            </div>
          </div>
        </div>
      )}
      {showCoding && (
        <div className="App app-overlay-root dark">
          <div className="client-info-overlay" onClick={closeLandingOverlay}>
            <div
              className="client-info-panel client-info-panel--landing"
              role="dialog"
              aria-modal="true"
              aria-labelledby="overlay-heading"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="client-info-close-wrapper">
                <button
                  type="button"
                  className="client-info-close-btn"
                  onClick={closeLandingOverlay}
                  aria-label="Back"
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                    <path d="M12.5 4L7 10l5.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  Back
                </button>
              </div>
              <div className="landing-overlay-content">
                <AutomatedMiscOverlay t={t} clientCodingRows={clientCodingRows} counsellorCodingRows={counsellorCodingRows} />
              </div>
            </div>
          </div>
        </div>
      )}
      {showLogin && ENABLE_AUTH && (
        <Suspense fallback={null}>
          <AuthLoginOverlay onDismiss={handleDismiss} onSuccess={handleAuthSuccess} />
        </Suspense>
      )}
      <div className="app-overlay-root dark">
        <Suspense fallback={null}>
          <ExpandedSectionModal
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
            t={t}
            personaList={clientProfilesData.personaList}
            metadataEntries={clientProfilesData.metadataEntries}
            motivationList={clientProfilesData.motivationList}
            barrierList={clientProfilesData.barrierList}
            nextSteps={clientProfilesData.nextSteps}
            transcriptLines={clientProfilesData.transcriptLines}
          />
        </Suspense>
      </div>
    </AuthContext.Provider>
  );
}

export default AuthWrapper;
